import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  TextField,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Typography,
  Chip,
  Grid
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Save as SaveIcon,
  Cancel as CancelIcon
} from '@mui/icons-material';
import * as api from '../../api/api';

function TagsTab() {
  const [tags, setTags] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ name: '' });
  const [dialogOpen, setDialogOpen] = useState(false);
  const [newTag, setNewTag] = useState({ name: '' });

  const fetchTags = async () => {
    try {
      setLoading(true);
      const response = await api.getTags();
      console.log('TagsTab: Raw API response:', response);
      
      // Handle different response formats
      let tagsData = [];
      if (Array.isArray(response)) {
        tagsData = response;
      } else if (response && Array.isArray(response.tags)) {
        tagsData = response.tags;
      } else {
        console.warn('TagsTab: Unexpected response format:', response);
        tagsData = [];
      }
      
      console.log('TagsTab: Processed tags:', tagsData);
      setTags(tagsData);
      setError('');
    } catch (err) {
      console.error('Error fetching tags:', err);
      setError('Failed to load tags');
      setTags([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTags();
  }, []);

  const handleEdit = (tag) => {
    setEditingId(tag.id);
    setEditForm({ name: tag.name });
  };

  const handleSaveEdit = async () => {
    if (!editForm.name.trim()) {
      setError('Tag name is required');
      return;
    }

    try {
      const response = await api.updateTag(editingId, editForm);
      console.log('Tag updated:', response);
      
      setEditingId(null);
      setEditForm({ name: '' });
      await fetchTags();
      setError('');
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('tagUpdated', {
        detail: { tagId: editingId, tagData: editForm }
      }));
      
    } catch (err) {
      console.error('Error updating tag:', err);
      setError('Failed to update tag');
    }
  };

  const handleCancelEdit = () => {
    setEditingId(null);
    setEditForm({ name: '' });
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this tag?')) {
      try {
        const response = await api.deleteTag(id);
        console.log('Tag deleted:', response);
        
        await fetchTags();
        setError('');
        
        // Dispatch event to notify other components
        window.dispatchEvent(new CustomEvent('tagDeleted', {
          detail: { tagId: id }
        }));
        
      } catch (err) {
        console.error('Error deleting tag:', err);
        setError('Failed to delete tag');
      }
    }
  };

  const handleAddNew = async () => {
    if (!newTag.name.trim()) {
      setError('Tag name is required');
      return;
    }

    try {
      console.log('Creating tag from AdminPage:', newTag);
      const response = await api.createTag(newTag);
      console.log('Tag creation response from AdminPage:', response);
      
      setDialogOpen(false);
      setNewTag({ name: '' });
      await fetchTags();
      setError('');
      
      // Dispatch event to notify other components
      window.dispatchEvent(new CustomEvent('tagCreated', {
        detail: { tagData: response }
      }));
      
    } catch (err) {
      console.error('Error creating tag:', err);
      setError('Failed to create tag');
    }
  };

  const filteredTags = tags
    .filter(tag => 
      tag.name.toLowerCase().includes(searchTerm.toLowerCase())
    )
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <Box>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6">Tags Management</Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setDialogOpen(true)}
          sx={{ bgcolor: '#9e9e9e', '&:hover': { bgcolor: '#757575' } }}
        >
          Add Tag
        </Button>
      </Box>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12}>
          <TextField
            label="Search Tags"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            fullWidth
            size="small"
          />
        </Grid>
      </Grid>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <TableContainer component={Paper} variant="outlined">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Name</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={2} align="center">Loading...</TableCell>
              </TableRow>
            ) : filteredTags.length === 0 ? (
              <TableRow>
                <TableCell colSpan={2} align="center">
                  {searchTerm ? 'No tags match your search' : 'No tags found'}
                </TableCell>
              </TableRow>
            ) : (
              filteredTags.map((tag) => (
                <TableRow key={tag.id}>
                  <TableCell>
                    {editingId === tag.id ? (
                      <TextField
                        value={editForm.name}
                        onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                        size="small"
                        fullWidth
                      />
                    ) : (
                      <Chip
                        label={tag.name}
                        size="small"
                        sx={{
                          bgcolor: (t) => t.palette.mode === 'dark' ? '#3a3a3a' : '#e0e0e0',
                          color: (t) => t.palette.mode === 'dark' ? '#d0d0d0' : '#424242',
                          '&:hover': { bgcolor: (t) => t.palette.mode === 'dark' ? '#4a4a4a' : '#d5d5d5' }
                        }}
                      />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    {editingId === tag.id ? (
                      <Box>
                        <IconButton
                          onClick={handleSaveEdit}
                          color="primary"
                          size="small"
                        >
                          <SaveIcon />
                        </IconButton>
                        <IconButton
                          onClick={handleCancelEdit}
                          color="secondary"
                          size="small"
                        >
                          <CancelIcon />
                        </IconButton>
                      </Box>
                    ) : (
                      <Box>
                        <IconButton
                          onClick={() => handleEdit(tag)}
                          sx={{ color: '#9e9e9e' }}
                          size="small"
                        >
                          <EditIcon />
                        </IconButton>
                        <IconButton
                          onClick={() => handleDelete(tag.id)}
                          color="error"
                          size="small"
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Box>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Add New Tag Dialog */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add New Tag</DialogTitle>
        <DialogContent>
          <TextField
            label="Tag Name"
            value={newTag.name}
            onChange={(e) => setNewTag({ ...newTag, name: e.target.value })}
            fullWidth
            margin="normal"
            required
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleAddNew} variant="contained">Add</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}

export default TagsTab;